/** Pure film title → catalog match ranking (no Angular). */

export interface SlateFilmCandidate {
    id: string;
    title: string;
    score: number;
    /** Optional approval / publish status from list payload */
    status?: string;
}

export type SlateFilmPick =
    | { kind: 'exact'; film: SlateFilmCandidate }
    | { kind: 'ambiguous'; films: SlateFilmCandidate[] }
    | { kind: 'none' };

export function rankFilmMatches(
    query: string,
    films: Array<{
        _id?: string;
        id?: string;
        title?: string;
        status?: string;
        latestSeasonStatus?: string;
    }>
): SlateFilmCandidate[] {
    const q = query.toLowerCase().trim();
    if (!q) return [];

    return films
        .map((f) => {
            const id = String(f._id || f.id || '');
            const title = String(f.title || '').trim();
            const status = String(
                f.status || f.latestSeasonStatus || ''
            ).trim();
            const tl = title.toLowerCase();
            let score = 0;
            if (!id || !tl) return { id, title, score: 0, status };
            if (tl === q) score = 100;
            else if (tl.startsWith(q)) score = 85;
            else if (tl.includes(q)) score = 65;
            else {
                // Skip 1-char tokens ("R" matches almost every title)
                const qt = q.split(/\s+/).filter((tok) => tok.length >= 2);
                const tt = tl.split(/\s+/).filter((w) => w.length >= 2);
                if (qt.length) {
                    const hits = qt.filter((tok) =>
                        tt.some((w) => w.includes(tok) || tok.includes(w))
                    ).length;
                    if (hits) score = Math.min(55, (hits / qt.length) * 55);
                }
            }
            return { id, title, score, status: status || undefined };
        })
        .filter((x) => x.score > 0)
        .sort((a, b) => b.score - a.score || a.title.localeCompare(b.title));
}

/** Clarify chip: human title (+ status). Id not shown — humans hate hex. */
export function clarifyTitleChip(
    film: { id: string; title: string; status?: string },
    prefix: string
): string {
    const st = (film.status || '').trim();
    return st
        ? `${prefix} "${film.title}" · ${st}`
        : `${prefix} "${film.title}"`;
}

export function resolveFilmPick(
    ranked: SlateFilmCandidate[],
    opts?: { strict?: boolean }
): SlateFilmPick {
    if (!ranked.length) return { kind: 'none' };
    const exact = ranked.filter((f) => f.score >= 100);
    if (exact.length === 1) return { kind: 'exact', film: exact[0] };
    if (exact.length > 1) {
        return { kind: 'ambiguous', films: exact.slice(0, 8) };
    }

    const top = ranked[0];
    const second = ranked[1];
    if (
        top.score >= 85 &&
        (!second || top.score - second.score >= 15 || ranked.length === 1)
    ) {
        return { kind: 'exact', film: top };
    }
    const strong = ranked.filter((f) => f.score >= 85);
    if (strong.length === 1) return { kind: 'exact', film: strong[0] };
    if (strong.length > 1) {
        return { kind: 'ambiguous', films: strong.slice(0, 8) };
    }

    // Open / privacy path: ignore weak fuzzy noise
    if (opts?.strict) {
        return { kind: 'none' };
    }

    // Analytics / detail lookup: allow soft include / token hits
    if (ranked.length === 1 && top.score >= 55) {
        return { kind: 'exact', film: top };
    }
    return { kind: 'ambiguous', films: ranked.slice(0, 5) };
}

/** Keep strong title hits only (filmmaker open must not list unrelated titles). */
export function filterStrongTitleMatches(
    ranked: SlateFilmCandidate[],
    minScore = 85
): SlateFilmCandidate[] {
    const strong = ranked.filter((f) => f.score >= minScore);
    return strong.length ? strong : ranked.filter((f) => f.score >= 100);
}
